import { hasStrongAuth } from './strong';
import type { AuthorizedUser } from './_internal';

// Auth states this module supports:
//   - Logged out:  isLoggedIn=false, isInitialMultiFactorAuthenticationRequired=false  → must log in
//   - Partial:     isLoggedIn=false, isInitialMultiFactorAuthenticationRequired=true   → must complete MFA
//   - Fully in:    isLoggedIn=true,  isInitialMultiFactorAuthenticationRequired=false  → good to go
//
// Two-boundary pattern: outer `Auth.Boundary` uses `hasInitiatedAuth` so partial users pass through
// and reach the inner boundary, which uses `hasAuth` to redirect them to the MFA step-up flow.

export function hasInitiatedAuth<User>(mapUser: (user: User) => AuthorizedUser): (user: User) => boolean;
export function hasInitiatedAuth<User extends AuthorizedUser = AuthorizedUser>(): (user: User) => boolean;
export function hasInitiatedAuth<User>(mapUser?: (user: User) => AuthorizedUser) {
  return (user: User) => {
    const u = mapUser ? mapUser(user) : (user as unknown as AuthorizedUser);

    const isImpersonating = u.impersonatorEmail && u.impersonatorUserToken;
    if (isImpersonating && u.isLoggedIn) {
      return true;
    }

    if (u.isUserBlocked) return false;

    if (!u.isLoggedIn && u.isInitialMultiFactorAuthenticationRequired) {
      return true;
    }

    if (!u.isLoggedIn) return false;
    if (!u.email && !u.userToken) return false;
    if (u.isInitialMultiFactorAuthenticationRequired) return false;

    return true;
  };
}

export function hasAuth<User>(mapUser: (user: User) => AuthorizedUser): (user: User) => boolean;
export function hasAuth<User extends AuthorizedUser = AuthorizedUser>(): (user: User) => boolean;
export function hasAuth<User>(mapUser?: (user: User) => AuthorizedUser) {
  return (user: User) => {
    const u = mapUser ? mapUser(user) : (user as unknown as AuthorizedUser);

    const isImpersonating = u.impersonatorEmail && u.impersonatorUserToken;
    if (isImpersonating && u.isLoggedIn) {
      return true;
    }

    if (u.isUserBlocked) return false;

    if (!u.isLoggedIn) return false;
    if (!u.email && !u.userToken) return false;
    if (u.isInitialMultiFactorAuthenticationRequired) return false;

    return true;
  };
}

// Bundle helper for apps doing the full boundary pattern — share one mapUser across all predicates.
type AuthPredicates<User> = {
  hasInitiatedAuth: (user: User) => boolean;
  hasAuth: (user: User) => boolean;
  hasStrongAuth: (user: User) => boolean;
};

export function createAuthPredicates<User>(mapUser: (user: User) => AuthorizedUser): AuthPredicates<User>;
export function createAuthPredicates<User extends AuthorizedUser = AuthorizedUser>(): AuthPredicates<User>;
export function createAuthPredicates<User>(mapUser?: (user: User) => AuthorizedUser): AuthPredicates<User> {
  return {
    hasInitiatedAuth: mapUser ? hasInitiatedAuth(mapUser) : (hasInitiatedAuth() as (user: User) => boolean),
    hasAuth: mapUser ? hasAuth(mapUser) : (hasAuth() as (user: User) => boolean),
    hasStrongAuth: mapUser ? hasStrongAuth(mapUser) : (hasStrongAuth() as (user: User) => boolean),
  };
}
